import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpBackend,
  HttpClient,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as moment from 'moment-timezone';
import { environment } from 'src/environments/environment';

@Injectable()
export class CityAutocompleteInterceptor implements HttpInterceptor {
  private directHttp: HttpClient;

  constructor(backend: HttpBackend) {
    // HttpBackend bypasses interceptor chain to avoid infinite loops
    this.directHttp = new HttpClient(backend);
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Check if this request is a city autocomplete query
    const isCityAutocomplete =
      req.url.includes('astro-ai/fetch_city_with_latLon') ||
      req.url.includes('astro-ai/form-autocomplete-city-name');

    if (!isCityAutocomplete) {
      return next.handle(req);
    }

    // Extract search query from request body
    let searchTerm = '';
    if (req.body) {
      let bodyObj: any = req.body;
      if (typeof bodyObj === 'string') {
        try {
          bodyObj = JSON.parse(bodyObj);
        } catch {
          bodyObj = {};
        }
      }
      searchTerm =
        bodyObj?.searchcondition?.search_string ||
        bodyObj?.searchcondition?.city ||
        bodyObj?.city ||
        bodyObj?.search_string ||
        '';
    }

    // If query is empty or too short, return empty success response
    if (
      !searchTerm ||
      typeof searchTerm !== 'string' ||
      searchTerm.trim().length < 2
    ) {
      return of(
        new HttpResponse({
          status: 200,
          body: {
            status: 'success',
            res: [],
          },
        })
      );
    }

    const query = searchTerm.trim();

    // Query Open-Meteo Geocoding API first for rich city, coordinates, timezone, and population data
    const openMeteoUrl = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
      query
    )}&count=10&language=en&format=json`;

    return this.directHttp.get<any>(openMeteoUrl).pipe(
      switchMap((openMeteoRes: any) => {
        const results: any[] = openMeteoRes?.results || [];
        if (results.length > 0) {
          const formatted = results.map((item: any) => {
            const adminParts = [item.name, item.admin1, item.country].filter(
              Boolean
            );
            const title = adminParts.join(', ');

            const latNum = Number(item.latitude);
            const lngNum = Number(item.longitude);
            const latStr = latNum.toFixed(3);
            const lngStr = lngNum.toFixed(3);
            const tzStr = item.timezone || 'UTC';
            const popStr =
              item.population != null && item.population > 0
                ? ` · pop ${Number(item.population).toLocaleString('en-US')}`
                : '';
            const subtitle = `${latStr}, ${lngStr} · ${tzStr}${popStr}`;
            const displayVal = `${title}\n${subtitle}`;

            let offsetStr = '+00:00';
            let decimalOffset = 0;
            try {
              offsetStr = moment.tz(tzStr).format('Z');
              decimalOffset = moment.tz(tzStr).utcOffset() / 60;
            } catch {
              offsetStr = '+00:00';
              decimalOffset = 0;
            }

            return {
              key: {
                lat: latNum,
                lng: lngNum,
                label: title,
                city: item.name || title,
                name: item.name,
                country: item.country,
                region: item.admin1,
                timezone_val: decimalOffset,
                timezone: {
                  name: tzStr,
                  offset_string: offsetStr,
                  utcOffset: offsetStr,
                },
              },
              val: displayVal,
            };
          });

          return of(
            new HttpResponse({
              status: 200,
              body: {
                status: 'success',
                res: formatted,
              },
            })
          );
        }

        // Fallback to internal fetch_lat_long endpoint if Open-Meteo returned 0 results
        return this.fallbackFetchLatLong(query);
      }),
      catchError(() => {
        // Fallback to internal fetch_lat_long endpoint on any Open-Meteo error
        return this.fallbackFetchLatLong(query);
      })
    );
  }

  private fallbackFetchLatLong(query: string): Observable<HttpEvent<any>> {
    const rawBaseUrl =
      environment.api_url || 'https://api.divineinfinitebeing.com/';
    const baseUrl = rawBaseUrl.endsWith('/') ? rawBaseUrl : `${rawBaseUrl}/`;
    const targetUrl = baseUrl.includes('cloudfront.net')
      ? 'https://api.divineinfinitebeing.com/astro-ai/fetch_lat_long'
      : `${baseUrl}astro-ai/fetch_lat_long`;

    const payload = {
      access_key: environment.positionstack_api_key,
      city: query,
    };

    return this.directHttp.post<any>(targetUrl, payload).pipe(
      map((response: any) => {
        const rawList: any[] = response?.res?.data || response?.data || [];
        const formatted = rawList.map((item: any) => {
          const latNum = Number(item.latitude);
          const lngNum = Number(item.longitude);
          const latStr = latNum.toFixed(3);
          const lngStr = lngNum.toFixed(3);
          const tzStr = item.timezone_module?.name || 'UTC';
          const offsetStr =
            item.timezone_module?.offset_string || '+00:00';

          const title =
            item.label ||
            [item.locality || item.name, item.region, item.country]
              .filter(Boolean)
              .join(', ') ||
            query;

          const subtitle = `${latStr}, ${lngStr} · ${tzStr}`;
          const displayVal = `${title}\n${subtitle}`;

          const sign = offsetStr.startsWith('-') ? -1 : 1;
          const rawTime = offsetStr.replace(/^[+-]/, '');
          const [h, m] = (rawTime || '0:0').split(':').map(Number);
          const decimalOffset =
            ((h || 0) + (m || 0) / 60) * sign;

          return {
            key: {
              lat: latNum,
              lng: lngNum,
              label: title,
              city: item.locality || item.name || title,
              name: item.name,
              country: item.country,
              region: item.region,
              timezone_val: decimalOffset,
              timezone: {
                name: tzStr,
                offset_string: offsetStr,
                utcOffset: offsetStr,
              },
            },
            val: displayVal,
          };
        });

        return new HttpResponse({
          status: 200,
          body: {
            status: 'success',
            res: formatted,
          },
        });
      }),
      catchError((error) => {
        console.error('CityAutocompleteInterceptor fallback error:', error);
        return of(
          new HttpResponse({
            status: 200,
            body: {
              status: 'success',
              res: [],
            },
          })
        );
      })
    );
  }
}
